import { IFirstStageValuesCushingV2 } from '../../../../../user/caseDraft/stages/cushing-v2/firstStage/FirstStage.types';

import { observer } from 'mobx-react-lite';
import { Divider, Grid, Typography } from '@mui/material';
import { Input } from '../../../../../../shared/ui/form/input/Input';

import { CheckedIconContainer } from '../../../checkedIcon/CheckedIcon';
import { BulletList } from '../../../bulletList/BulletList';
import { useCaseStore } from '../../../../../../../store/hooks';
import { Text } from '../../../Description.styled';

export const FirstStage = observer(() => {
  const caseStore = useCaseStore();
  const { firstStage } = caseStore as { firstStage: IFirstStageValuesCushingV2 };

  if (!firstStage) {
    return null;
  }

  return (
    <Grid container justifyContent={'space-between'} width={'100%'} mt={6}>
      <Typography variant={'h5'}>
        Obraz kliniczny, powikłania hiperkortyzolemii oraz choroby współistniejące w okresie ustalenia rozpoznania:
      </Typography>
      {firstStage.symptoms?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant={'h6'} mt={2}>
            Objawy
          </Typography>
          <BulletList items={firstStage.symptoms} />
        </Grid>
      )}
      {firstStage.psychiatricDisorders?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant={'subtitle1'} mt={2}>
            Zaburzenia psychiatryczne:
          </Typography>
          <Grid container>
            {firstStage.psychiatricDisorders.map(el => (
              <Grid key={el} item display={'flex'} alignItems={'center'} justifyItems={'center'} pr={2}>
                <CheckedIconContainer />
                <Text>{el}</Text>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      <Typography variant={'h6'} mt={2}>
        Badanie przedmiotowe
      </Typography>
      <Grid container gap={2} display={'flex'} alignItems={'center'} mt={2}>
        <Grid item xs={2.5}>
          <Input
            disabled
            variant={'standard'}
            fullWidth={true}
            required={true}
            name={'weight'}
            label={'Masa ciała (kg)'}
            type={'text'}
            maxLength={2}
            value={firstStage.weight}
          />
        </Grid>
        <Grid item xs={2.5}>
          <Input
            disabled
            variant={'standard'}
            fullWidth={true}
            required={true}
            name={'height'}
            label={'Masa ciała (kg)'}
            type={'number'}
            value={firstStage?.height}
          />
        </Grid>
        <Grid item xs={2.5}>
          <Input
            disabled
            variant={'standard'}
            fullWidth={true}
            name={'height'}
            label={'BMI'}
            type={'number'}
            value={firstStage.bmi}
          />
        </Grid>
        <Grid item xs={2.5}>
          <Input
            disabled
            variant={'standard'}
            fullWidth={true}
            required={true}
            name={'bloodPressure'}
            label={'Ciśnienie tętnicze'}
            type={'text'}
            value={firstStage.bloodPressure}
          />
        </Grid>
        <Grid item xs={2.5}>
          <Input
            disabled
            variant={'standard'}
            fullWidth={true}
            required={true}
            name={'bmi'}
            label={'Tętno'}
            type={'number'}
            value={firstStage.heartRate}
          />
        </Grid>
      </Grid>
      {firstStage.gender && (
        <Grid item xs={12} mt={2}>
          <Typography variant={'subtitle1'} mt={2}>
            Płeć
          </Typography>
          <Grid item display={'flex'} alignItems={'center'} justifyItems={'center'} pr={2}>
            <CheckedIconContainer />
            <Text>{firstStage.gender === 'F' ? 'Kobieta' : firstStage.gender === 'M' ? 'Mężczyzna' : 'Inna'}</Text>
          </Grid>
        </Grid>
      )}
      {firstStage.physicalExaminations?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant={'subtitle1'} mt={2}>
            Badania przedmiotowe:
          </Typography>
          <Grid container>
            {firstStage.physicalExaminations.map(el => (
              <Grid key={el} item display={'flex'} alignItems={'center'} justifyItems={'center'} pr={2}>
                <CheckedIconContainer />
                <Text>{el}</Text>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      {firstStage.complicationsOfHypercortisolism?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant={'h6'} mt={2}>
            Powikłania hiperkortyzolemii
          </Typography>
          <BulletList items={firstStage.complicationsOfHypercortisolism} />
        </Grid>
      )}
      {firstStage.complicationsOfHypercortisolism?.includes('powikłania zakrzepowo-zatorowe') &&
        firstStage.thromboembolicComplications?.length > 0 && (
          <Grid item xs={12}>
            <Typography variant={'subtitle1'} mt={2}>
              Opis powikłań zakrzepowo-zatorowych:
            </Typography>
            <Grid container>
              {firstStage.thromboembolicComplications.map(el => (
                <Grid key={el} item display={'flex'} alignItems={'center'} justifyItems={'center'} pr={2}>
                  <CheckedIconContainer />
                  <Text>{el}</Text>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      {firstStage.complicationsOfHypercortisolism?.includes('złamania') && (
        <Grid item xs={12} mt={2}>
          <Input
            disabled
            variant={'standard'}
            fullWidth={true}
            required={true}
            name={'descriptionOfFractures'}
            label={'Opis Złamań'}
            type={'text'}
            multiline={true}
            value={firstStage.descriptionOfFractures}
          />
        </Grid>
      )}
      {firstStage.complicationsOfHypercortisolism?.includes('zaburzenia gospodarki węglowodanowej') &&
        firstStage.carbohydrateMetabolismDisorders?.length > 0 && (
          <Grid item xs={12} mt={2}>
            <Typography variant={'subtitle1'} mt={2}>
              Opis powikłań zakrzepowo-zatorowych:
            </Typography>
            <Grid container>
              {firstStage.carbohydrateMetabolismDisorders.map(el => (
                <Grid item display={'flex'} alignItems={'center'} justifyItems={'center'} pr={2} key={el}>
                  <CheckedIconContainer />
                  <Text>{el}</Text>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      {firstStage.comorbidities?.length > 0 && (
        <Grid item xs={12} mt={2}>
          <Typography variant={'h6'} mt={2}>
            Choroby współistniejące
          </Typography>
          <Grid container flexDirection={'column'}>
            {firstStage.comorbidities.map((el, index) => (
              <Grid gap={2} item display={'flex'} alignItems={'center'} justifyItems={'center'} key={index}>
                <Text>{index + 1}</Text>
                <Input
                  disabled
                  variant={'standard'}
                  fullWidth={true}
                  name={'comorbidities'}
                  label={'Choroba'}
                  type={'text'}
                  multiline={true}
                  value={el}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} my={4}>
        <Divider sx={{ width: '100%' }} />
      </Grid>
    </Grid>
  );
});
